import { useCallback, useMemo } from "react";
import { useGetDashboardQuery } from "@webcore/services/api/api";
import { FullViewSpinner } from "@webcore/components/core/FullViewSpinner/FullViewSpinner";
import { WidgetDataResponse } from "@webcore/models/coreModels";
import CustomCodeRunner from "@webcore/components/domain/dashboard/renderers/custom/CustomCodeRunner";

interface CustomViewRendererProps {
  dashboardId: string;
  viewId: string;
  dataPipelineConfigurationId: string;
}

/**
 * Shared renderer for a custom dashboard view's code. In the MFE model it runs inside the ops
 * container under the read-only MFE session. All requests go through the same-origin /api/v1 path
 * so the read-only cookie is sent.
 */
export default function CustomViewRenderer({ dashboardId, viewId, dataPipelineConfigurationId }: CustomViewRendererProps) {
  const { data: dashboard, isLoading, isError } = useGetDashboardQuery({ id: dashboardId });

  const viewCode = useMemo(() => {
    if (dashboard === undefined) {
      return null;
    }

    const view = dashboard.dashboardViews.find(v => v.id === viewId);
    return view?.viewCode ?? null;
  }, [dashboard, viewId]);

  const kpiDefinitions = useMemo(() => dashboard?.kpiDefinitions ?? [], [dashboard]);

  const fetchKpiData = useCallback(async (kpiId: string, filters?: Record<string, string>): Promise<WidgetDataResponse> => {
    const params = new URLSearchParams(filters ?? {});
    const queryString = params.toString();
    const url = `/api/v1/teams/active/dashboards/${dashboardId}/kpis/${kpiId}/data/${encodeURIComponent(dataPipelineConfigurationId)}${queryString ? `?${queryString}` : ""}`;
    const response = await fetch(url, { credentials: "include" });

    if (!response.ok) {
      throw new Error(`Failed to fetch KPI data: ${response.status} ${response.statusText}`);
    }

    const envelope = await response.json();
    return envelope.data;
  }, [dashboardId, dataPipelineConfigurationId]);

  if (isLoading) {
    return <FullViewSpinner />;
  }

  if (isError) {
    return <div className="widget-error">Failed to load dashboard.</div>;
  }

  if (viewCode === null) {
    return <div className="widget-error">No custom view code found.</div>;
  }

  return (
    <div className="w-100 vh-100 overflow-auto">
      <CustomCodeRunner code={viewCode} label="View Error" scope={{ kpiDefinitions, fetchKpiData }} />
    </div>
  );
}
